import { projectedMrrQuery } from "@/api/reporting";
import { DataTable } from "@/components/data-table/DataTable";
import { DataTableBody } from "@/components/data-table/DataTableBody";
import { DataTableHeader } from "@/components/data-table/DataTableHeader";
import { StyledLink } from "@/components/link/StyledLink";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { centsToDollars } from "@common/utils/currency";
import { formatDollars } from "@common/utils/i18n";
import { Box, Select } from "@givenwell/components";
import { colors } from "@givenwell/theme";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { fallback, object, optional, string } from "valibot";

export const Route = createFileRoute("/admin/reports/$countryCode/projected-mrr")({
  component: ProjectedMrrReportPage,
  validateSearch: object({
    currency: fallback(optional(string(), ""), "nzd"),
  }),
  beforeLoad: () => ({
    breadcrumb: "Projected MRR",
  }),
});

const formatCentsAsDollars = (cents: number | null | undefined, currency: string) => {
  return cents ? formatDollars(centsToDollars(cents, currency), currency, { maximumFractionDigits: 0 }) : "";
};

function ProjectedMrrReportPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const query = useQuery(projectedMrrQuery());

  // Get all data and available currencies
  const allData = query.data ?? [];
  const availableCurrencies = Array.from(new Set(allData.map(row => row.currency)))
    .filter(Boolean)
    .sort();

  // Determine current currency - use first available if current selection is invalid
  let { currency } = search;
  if (!currency || !availableCurrencies.includes(currency as any)) {
    currency = availableCurrencies[0] || "NZD";
  }

  // Filter data by selected currency and calculate totals
  const filteredData = allData.filter(row => row.currency === currency);

  const totals = filteredData.reduce(
    (acc, row) => ({
      members: acc.members + (row.member_count ?? 0),
      monthly: acc.monthly + (row.freq1?.cents ?? 0),
      threeMonthly: acc.threeMonthly + (row.freq3?.cents ?? 0),
      sixMonthly: acc.sixMonthly + (row.freq6?.cents ?? 0),
      annual: acc.annual + (row.freq12?.cents ?? 0),
      grandTotal: acc.grandTotal + (row.grand_total?.cents ?? 0),
    }),
    { members: 0, monthly: 0, threeMonthly: 0, sixMonthly: 0, annual: 0, grandTotal: 0 },
  );

  // Calculate average MRR per member
  const avgMrrPerMember = totals.members > 0 ? Math.round(totals.grandTotal / totals.members / 100) : 0;

  // Create totals row
  const totalsRow = {
    supporter_id: "TOTALS",
    supporter_name: "TOTALS",
    member_count: totals.members,
    freq1: { cents: totals.monthly },
    freq3: { cents: totals.threeMonthly },
    freq6: { cents: totals.sixMonthly },
    freq12: { cents: totals.annual },
    grand_total: { cents: totals.grandTotal },
    currency,
  } as any;

  // Add totals row to top of data
  const dataWithTotals = [totalsRow, ...filteredData];

  function handleCurrencyChange(newCurrency: string) {
    navigate({
      to: ".",
      params: true,
      search: { ...search, currency: newCurrency },
    });
  }

  return (
    <PageWrapper title="Projected MRR">
      <Box css={{ spaceY: 16 }}>
        <DataTable>
          <DataTableHeader hideSearch hideToggle>
            <Select size="sm" value={currency} onValueChange={handleCurrencyChange} css={{ maxW: 200 }}>
              {availableCurrencies.map(curr => (
                <option key={curr} value={curr}>
                  {curr}
                </option>
              ))}
            </Select>
          </DataTableHeader>
          <DataTableBody
            data={dataWithTotals}
            dataKey="supporter_id"
            status={query.status}
            rounded="bottom"
            columns={[
              {
                header: "Supporter",
                cell: r => {
                  if (r.supporter_id === "TOTALS") {
                    return <strong>TOTALS</strong>;
                  }
                  return (
                    <StyledLink to={`/admin/supporters/$supporterId`} params={{ supporterId: r.supporter_id }}>
                      {r.supporter_name}
                    </StyledLink>
                  );
                },
                getCellCss: r => (r.supporter_id === "TOTALS" ? { fontWeight: 700 } : {}),
              },
              {
                header: "Monthly",
                cell: r => formatCentsAsDollars(r.freq1?.cents, r.currency),
                textAlign: "right",
                css: { textAlign: "right" },
                getCellCss: r => (r.supporter_id === "TOTALS" ? { fontWeight: 700 } : {}),
              },
              {
                header: "3-Monthly",
                cell: r => formatCentsAsDollars(r.freq3?.cents, r.currency),
                textAlign: "right",
                css: { textAlign: "right" },
                getCellCss: r => (r.supporter_id === "TOTALS" ? { fontWeight: 700 } : {}),
              },
              {
                header: "6-Monthly",
                cell: r => formatCentsAsDollars(r.freq6?.cents, r.currency),
                textAlign: "right",
                css: { textAlign: "right" },
                getCellCss: r => (r.supporter_id === "TOTALS" ? { fontWeight: 700 } : {}),
              },
              {
                header: "Annual",
                cell: r => formatCentsAsDollars(r.freq12?.cents, r.currency),
                textAlign: "right",
                css: { textAlign: "right" },
                getCellCss: r => (r.supporter_id === "TOTALS" ? { fontWeight: 700 } : {}),
              },
              {
                header: "Total",
                cell: r => formatCentsAsDollars(r.grand_total?.cents, r.currency),
                textAlign: "right",
                css: { textAlign: "right", fontWeight: 700, pr: 16 },
              },
              {
                header: "Members",
                cell: "member_count",
                formatter: "number",
                textAlign: "right",
                css: { borderLeft: `1px solid ${colors.outline}` },
                getCellCss: r => (r.supporter_id === "TOTALS" ? { fontWeight: 700 } : {}),
              },
              {
                header: "Avg MRR/Member",
                cell: r => {
                  if (r.supporter_id === "TOTALS") {
                    return formatDollars(avgMrrPerMember, r.currency, { maximumFractionDigits: 0 });
                  }
                  const memberCount = r.member_count ?? 0;
                  const totalCents = r.grand_total?.cents ?? 0;
                  if (memberCount === 0 || totalCents === 0) return "";
                  const avg = Math.round(totalCents / memberCount / 100);
                  return formatDollars(avg, r.currency, { maximumFractionDigits: 0 });
                },
                textAlign: "right",
                css: { textAlign: "right" },
                getCellCss: r => (r.supporter_id === "TOTALS" ? { fontWeight: 700 } : {}),
              },
            ]}
          />
        </DataTable>
      </Box>
    </PageWrapper>
  );
}
